import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { HelpAccordion } from "@/components/HelpAccordion";
import uploadShot from "@/assets/help/upload.png.asset.json";
import wardrobeShot from "@/assets/help/wardrobe-add.png.asset.json";
import modelShot from "@/assets/help/customise-model.png.asset.json";

const SUPPORT_EMAIL = "support@like2fit.com";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "Help — Like2Fit" },
      {
        name: "description",
        content:
          "How to use Like2Fit — getting started, lookbooks, wardrobe, customising your model, and troubleshooting.",
      },
    ],
  }),
  component: HelpPage,
});

function HelpPage() {
  const router = useRouter();
  const goBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({ to: "/" });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--background)" }}>
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3"
        style={{ background: "var(--background)", borderBottom: "0.5px solid var(--border)" }}
      >
        <button
          onClick={goBack}
          aria-label="Back"
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full"
          style={{ background: "transparent", border: "none" }}
        >
          <ArrowLeft size={20} style={{ color: "var(--l2f-ink)" }} />
        </button>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 24,
            color: "var(--l2f-ink)",
            margin: 0,
          }}
        >
          Help
        </h1>
      </header>

      <main className="px-5 pt-6 pb-24 max-w-2xl mx-auto">
        <HelpAccordion title="Getting started" id="getting-started">
          <p>
            Upload an inspiration photo. We'll show how the look sits on a model based on you, then
            surface the items so you can shop them. Tap any item to see retailers and prices.
          </p>
        </HelpAccordion>

        <HelpAccordion title="Seeing it on you" id="seeing-it-on-you">
          <p>
            Upload an inspiration photo. We'll show how that look sits on a model based on your
            features. The closer your model setup matches you, the closer the result will feel. If
            the first result doesn't look like you, open the Account tab and tap "Customise model",
            then regenerate the look.
          </p>
          <img
            src={uploadShot.url}
            alt="The inspiration upload surface"
            style={{ width: "100%", borderRadius: 12, marginTop: 4 }}
          />
        </HelpAccordion>

        <HelpAccordion title="Styled for you" id="styled-for-you">
          <p>
            Variations of the same look styled differently — useful when the original inspiration is
            close but not quite right. Each variation links through to shoppable products.
          </p>
        </HelpAccordion>

        <HelpAccordion title="From your wardrobe" id="from-your-wardrobe">
          <p>
            Add items you already own to your wardrobe, then generate looks that mix the inspiration
            with what you have. This answers "will this work with what I own?" without you having to
            think about it.
          </p>
          <img
            src={wardrobeShot.url}
            alt="Adding an item to your wardrobe"
            loading="lazy"
            style={{ width: "100%", borderRadius: 12, marginTop: 4 }}
          />
        </HelpAccordion>

        <HelpAccordion title="Making it look like you" id="customise-model">
          <p>
            Open the Account tab and tap "Customise model". Set skin tone, hair, body shape and
            height — future looks use this model. As a guest, you'll be prompted to sign up when you
            tap to customise.
          </p>
          <img
            src={modelShot.url}
            alt="The model customisation panel"
            loading="lazy"
            style={{ width: "100%", borderRadius: 12, marginTop: 4 }}
          />
        </HelpAccordion>

        <HelpAccordion title="Saving & sharing" id="saving-sharing">
          <p>
            Three options. Save to your account keeps the look in your Saved tab — needs an account.
            Email the look sends it to your inbox so you can come back later, no account required.
            Download saves the image to your device.
          </p>
        </HelpAccordion>

        <HelpAccordion title="Shopping the look" id="shopping">
          <p>
            Every look surfaces products at three budget tiers — cheaper, mid, premium. Set your
            budget in the Account tab and we'll filter what you see. "Exact match" means we found
            the actual item in the inspiration. "Similar item" means the closest in-stock
            alternative at your budget.
          </p>
        </HelpAccordion>

        <HelpAccordion title="Guest vs account" id="guest-vs-account">
          <p>
            <strong>Without an account:</strong> see looks on yourself, generate up to 10 looks a
            day, email any look to yourself.
          </p>
          <p>
            <strong>With an account:</strong> save unlimited looks, build a wardrobe, get
            personalised looks that mix in what you already own.
          </p>
        </HelpAccordion>

        <HelpAccordion title="Troubleshooting" id="troubleshooting">
          <p>
            <strong>My upload didn't work.</strong> Use a JPG or PNG under 10MB. A clear, well-lit
            photo of the full outfit works best. If it still fails, close the app and try once more.
          </p>
          <p>
            <strong>The model doesn't look like me.</strong> Open Account → Customise model, update
            your features, then tap Regenerate on the look.
          </p>
          <p>
            <strong>No products were found for my look.</strong> The inspiration may use unusual or
            out-of-stock items. Try a similar inspiration, or widen your budget in the Account tab.
          </p>
          <p>
            <strong>Something else is broken.</strong> Email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--l2f-coral)" }}>
              {SUPPORT_EMAIL}
            </a>{" "}
            and we'll take a look.
          </p>
        </HelpAccordion>

        <p
          className="text-sm text-center mt-10"
          style={{ color: "var(--muted-foreground)" }}
        >
          Looking for FAQ?{" "}
          <Link to="/faq" style={{ color: "var(--l2f-coral)" }}>
            See FAQ
          </Link>
        </p>
      </main>
    </div>
  );
}
